import {
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { organizationsApi, rolesApi } from "api";
import { getTranslation, toDataUrl, OutletType } from "common";
import { useInfiniteScroll, useIsMobile, useSnackbar, useTranslations, useVersions } from "hooks";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useOutletContext } from "react-router-dom";
import { Add, Delete, Edit } from "@mui/icons-material";
import { ConfirmDialog, RoleDialog } from "./dialogs";
import AvatarRem from "./AvatarRem";
import FlowentiButton from "./flowenti-ui-kit/FlowentiButton";
import OrganizationResourceTable, {
  OrganizationResourceCards,
  OrganizationResourceColumn,
} from "pages/organizations/OrganizationResources";
import emptyImage from "image/empty-roles.png";
import emptyImageDark from "image/empty-roles-dark.png";
import { SupportedUserDataTypes } from "hooks/useUserDataKeys";
import { Translations } from "model/messageModel";

interface RolesPanelProps {
  organizationId: number;
  administrator: boolean;
  onCreateRole?: () => void;
}
export interface Role {
  creation?: string;
  id?: number;
  icon?: string | null;
  idorganization?: number;
  name?: string;
  userscount?: number;
  activeprocesses?: Array<string | { [key: string]: unknown }>;
  processmodels?: Array<string | { [key: string]: unknown }>;
  [key: string]: any;
}
type Ref = { getRoles: Function } | undefined;

const INITIAL_PAGE = 1;
const PAGE_SIZE = 10;

const getUsersCount = (resource: any) =>
  Number(
    resource.usersnumber ??
    resource.userscount ??
    resource.usercount ??
    resource.assignedusers ??
    resource.assigneduserscount ??
    resource.users?.length ??
    0
  );

const hasDeletionDependencies = (role: Role | null) =>
  Boolean(role?.activeprocesses?.length || role?.processmodels?.length);

const getDependencyLabel = (dependency: string | { [key: string]: unknown }) => {
  if (typeof dependency === "string") {
    return dependency;
  }

  const label =
    dependency.name ||
    dependency.title ||
    dependency.label ||
    dependency.description ||
    dependency.id;

  return label ? String(label) : "";
};

const getDeletionDependencyMessage = (role: Role | null, translations: Translations) => {
  const activeProcesses = (role?.activeprocesses || [])
    .map(getDependencyLabel)
    .filter(Boolean);
  const processModels = (role?.processmodels || [])
    .map(getDependencyLabel)
    .filter(Boolean);
  const details = [
    activeProcesses.length
      ? `${getTranslation(translations, "generic.activeProcesses")}: ${activeProcesses.join(", ")}`
      : "",
    processModels.length
      ? `${getTranslation(translations, "generic.processModels")}: ${processModels.join(", ")}`
      : "",
  ].filter(Boolean);

  return `${getTranslation(translations, "the.role")}${role?.name ? ` "${role.name}"` : ""} ${getTranslation(translations, "generic.isAssociatedWith")} ${details.join(
    ` ${getTranslation(translations, "generic.and")} `
  )}. ${getTranslation(translations, "generic.doYouWantToProceed")}?`;
};

const RolesPanel = forwardRef<Ref, RolesPanelProps>(
  ({ organizationId, administrator, onCreateRole }, ref) => {
    const translations = useTranslations();
    const theme = useTheme();
    const emptyRolesImage = theme.palette.mode === "dark"
      ? emptyImageDark
      : emptyImage;
    // const user = useUser();
    // const rolesEvent = useRef<EventSource>(new EventSource(`${process.env.REACT_APP_BACKEND_BASE_URI}organizations/${organizationId}/roles/sse?jwt=${user.jwt}`));
    const { secondFilter: roleFilter } = useOutletContext<OutletType>();
    const [roles, setRoles] = useState<Array<Role>>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [roleEdit, setRoleEdit] = useState<Role | null>(null);
    const [rolePendingDelete, setRolePendingDelete] = useState<Role | null>(null);
    const [page, setPage] = useState<number>(INITIAL_PAGE);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const { sendSnack } = useSnackbar();
    const lastRoleRef = useInfiniteScroll(setPage, loading, hasMore);
    const mount = useRef<boolean>(false);
    const { getVersion, updateVersion } = useVersions();
    const isMobile = useIsMobile();

    const getFirstRoles = useCallback(async () => {
      setLoading(true);
      try {
        const queryPage = `?page=${INITIAL_PAGE}&pagesize=${PAGE_SIZE}`;
        const filter = `&name=${roleFilter}`;
        const { data } = await organizationsApi.get(
          `${organizationId}/roles/v2${queryPage}${filter}`
        );
        setHasMore(Number(data.pagination.curr_page) < Number(data.pagination.total_pages));
        // setRoles([...data.roles]);
        setRoles(JSON.parse(JSON.stringify(data.roles)));
        setPage(INITIAL_PAGE);
      } catch {
      } finally {
        setLoading(false);
      }
    }, [organizationId, roleFilter]);

    const getRoles = useCallback(
      async (query: string) => {
        if (page !== INITIAL_PAGE) {
          setLoading(true);
          try {
            const queryPage = `?page=${page}&pagesize=${PAGE_SIZE}`;
            const filter = `&name=${query}`;
            const { data } = await organizationsApi.get(
              `${organizationId}/roles/v2${queryPage}${filter}`
            );
            setHasMore(Number(data.pagination.curr_page) < Number(data.pagination.total_pages));
            setRoles((_roles) => [..._roles, ...data.roles]);
          } catch {
          } finally {
            setLoading(false);
          }
        }
      },
      [organizationId, page]
    );

    useImperativeHandle(
      ref,
      () => ({
        getRoles: getFirstRoles,
      }),
      [getFirstRoles]
    );

    useEffect(() => {
      getRoles(roleFilter);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getRoles]);

    useEffect(() => {
      if (organizationId && mount.current) {
        getFirstRoles();
      }
      else if (!mount.current) {
        mount.current = true;
        getFirstRoles();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId, roleFilter]);

    // useEffect(() => {
    //   if (!mount.current) {
    //     mount.current = true;
    //     getFirstRoles();
    //   }
    //   // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [organizationId]);

    useEffect(() => {
      return () => {
        mount.current = false;
        setPage(INITIAL_PAGE);
        setRoles([]);
      };
    }, [organizationId]);


    const handleSaveEdit = async (
      imageUrl: string | null,
      name: string | null
    ) => {
      try {
        let icon = null;
        if (imageUrl) {
          icon = await toDataUrl(imageUrl);
        }
        const updateApi = await rolesApi.put(`${roleEdit?.id}`, { name, icon });
        updateVersion(roleEdit?.id)
        setRoleEdit(null);
        sendSnack({
          message: `${updateApi.data.responsemessage}`,
          type: "success",
        });
        getFirstRoles();
      } catch {
      } finally {
      }
    };

    const performDeleteRole = async (targetRole: Role | null = roleEdit) => {
      if (targetRole) {
        try {
          const deleteApi = await rolesApi.delete(`${targetRole.id}`);
          sendSnack({
            message: `${deleteApi.data.responsemessage}`,
            type: "success",
          });
          getFirstRoles();
          setRoleEdit(null);
        } catch {
        } finally {
        }
      }
    };

    const handleDeleteRole = async (targetRole: Role | null = roleEdit) => {
      if (!targetRole) {
        return;
      }

      if (hasDeletionDependencies(targetRole)) {
        setRolePendingDelete(targetRole);
        return;
      }

      await performDeleteRole(targetRole);
    };


    const columns: OrganizationResourceColumn<Role>[] = [
      {
        id: "name",
        label: getTranslation(translations, "placeholders.role.name.label"),
        sortType: SupportedUserDataTypes.string,
        sortValue: (role) => role.name || "",
        render: ({ icon, name }) => (
          <Stack direction="row" alignItems="center" spacing={2}>
            <AvatarRem icon={icon || ""} />
            <Typography fontWeight={700}>{name}</Typography>
          </Stack>
        ),
      },
      {
        id: "users",
        label: getTranslation(translations, "generic.users.number"),
        sortType: SupportedUserDataTypes.number,
        sortValue: (role) => getUsersCount(role),
        render: (role) => (
          <Box
            component="span"
            sx={{
              borderRadius: 999,
              bgcolor: (theme) =>
                alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.22 : 0.12),
              border: "1px solid",
              borderColor: (theme) =>
                alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.7 : 0.35),
              color: (theme) =>
                theme.palette.mode === "dark"
                  ? theme.palette.primary.light
                  : theme.palette.primary.dark,
              px: 1.5,
              py: 0.5,
              fontWeight: 700,
            }}
          >
            {getUsersCount(role)}
          </Box>
        ),
      },
      {
        id: "actions",
        label: getTranslation(translations, "generic.actions"),
        align: "right",
        render: (role) =>
          administrator ? (
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Tooltip title={getTranslation(translations, "generic.edit_role.caption")}>
                <IconButton
                  id={`btn-modify-role-${role.id}`}
                  size="small"
                  color="secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setRoleEdit(role);
                  }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={getTranslation(translations, "generic.remove")}>
                <IconButton
                  id={`btn-delete-role-${role.id}`}
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteRole(role);
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          ) : null,
      },
    ];

    return (
      <React.Fragment>
        <Stack height="100%" overflow="hidden" flex={1}>
          {!loading && roles.length === 0 ? (
            <Stack height="100%" alignItems="center" justifyContent="center">
              <Box
                component="img"
                src={emptyRolesImage}
                alt="non ci sono ruoli"
                sx={{ width: 400, height: 400, objectFit: "contain" }}
              />
              <Stack direction={{ xs: "column", sm: "row" }} alignItems="center" spacing={1.5}>
                <Typography variant="h6">{getTranslation(translations, "generic.no.roles")}</Typography>
                {administrator && onCreateRole && (
                  <FlowentiButton
                    id="btn-empty-roles-card"
                    size="small"
                    variant="contained"
                    startIcon={<Add />}
                    onClick={onCreateRole}
                  >
                    {getTranslation(translations, "roles.button.create")}
                  </FlowentiButton>
                )}
              </Stack>
            </Stack>
          ) : isMobile ? (
            <OrganizationResourceCards
              id="roles-list"
              rows={roles}
              columns={columns}
              loading={loading}
              getRowKey={(role) => `role-${role.id}-${getVersion(role.id)}`}
              getRowId={(role) => `role-${role.id}`}
              canHover={() => administrator}
              onRowClick={(role) => {
                if (administrator) {
                  setRoleEdit(role);
                }
              }}
              getLastRowRef={(role, index) =>
                roles.length === index + 1 ? lastRoleRef : undefined
              }
            />
          ) : (
            <OrganizationResourceTable
              id="roles-list"
              rows={roles}
              columns={columns}
              loading={loading}
              getRowKey={(role) => `role-${role.id}-${getVersion(role.id)}`}
              getRowId={(role) => `role-${role.id}`}
              canHover={() => administrator}
              onRowClick={(role) => {
                if (administrator) {
                  setRoleEdit(role);
                }
              }}
              getLastRowRef={(role, index) =>
                roles.length === index + 1 ? lastRoleRef : undefined
              }
            />
          )}
        </Stack>
        <RoleDialog
          loading={loading}
          open={Boolean(roleEdit)}
          role={roleEdit}
          organizationId={organizationId}
          onUsersUpdate={getFirstRoles}
          onDelete={handleDeleteRole}
          onClose={() => setRoleEdit(null)}
          onSave={handleSaveEdit}
        />
        <ConfirmDialog
          open={Boolean(rolePendingDelete)}
          onClose={() => setRolePendingDelete(null)}
          title={getTranslation(translations, "generic.button.confirm") || "Conferma"}
          content={getDeletionDependencyMessage(rolePendingDelete, translations)}
          cancelText={getTranslation(translations, "user.buttoncancel.label") || "Annulla"}
          confirmText={getTranslation(translations, "generic.button.confirm") || "Conferma"}
          onConfirm={async () => {
            const targetRole = rolePendingDelete;
            setRolePendingDelete(null);
            await performDeleteRole(targetRole);
          }}
        />
      </React.Fragment>
    );
  }
);

export default RolesPanel;
